import { useEffect, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { PiUsersThreeThin } from 'react-icons/pi';
import EditBlogModal from './EditBlogModal';
import api from '../../services/Api';
import { BeatLoader } from 'react-spinners';
import { showSuccess, showError } from '../../components/Ui/ToastNotification';
import { useNavigate } from 'react-router-dom';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const ManageBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [filter, setFilter] = useState(null);
  const [sort, setSort] = useState('newest');
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  useEffect(() => {
    const fetchBlogsData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/blog', { params: { searchTerm: '' } });

        const actualBlogs = response.data.blogs.filter(
          item => item.title && item.title.trim() !== ''
        );

        const sanitizedBlogs = actualBlogs.map(blog => ({
          ...blog,
          content: sanitizeHtml(blog.content),
        }));

        setBlogs(sanitizedBlogs);
        setFilteredBlogs(sanitizedBlogs);
        showSuccess('Blogs loaded successfully');
      } catch (err) {
        setError(err.message || 'Failed to fetch blogs.');
        showError(err.message || 'Failed to fetch blogs.');
      } finally {
        setLoading(false);
      }
    };
    fetchBlogsData();
  }, []);

  useEffect(() => {
    let result = [...blogs];
    if (filter === 'featured') {
      result = result.filter(blog => blog.isFeatured);
    } else if (filter === 'not-featured') {
      result = result.filter(blog => !blog.isFeatured);
    }
    result.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sort === 'newest' ? dateB - dateA : dateA - dateB;
    });
    setFilteredBlogs(result);
    setCurrentPage(1);
  }, [blogs, filter, sort]);

  const toggleFilterMenu = () => setIsFilterMenuOpen(!isFilterMenuOpen);
  const toggleSortMenu = () => setIsSortMenuOpen(!isSortMenuOpen);
  const toggleActionMenu = index => {
    setActionMenuOpen(actionMenuOpen === index ? null : index);
  };

  const handleFilterChange = selectedFilter => {
    setFilter(selectedFilter);
    setIsFilterMenuOpen(false);
  };

  const handleSortChange = selectedSort => {
    setSort(selectedSort);
    setIsSortMenuOpen(false);
  };

  const handleActionClick = (action, blog) => {
    if (action === 'edit') {
      openEditModal(blog);
    } else if (action === 'delete') {
      deleteBlog(blog._id);
    }
    setActionMenuOpen(null);
  };

  const openEditModal = blog => {
    setSelectedBlog(blog);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedBlog(null);
  };

  const saveBlogChanges = async updatedBlog => {
    try {
      await api.patch(`/admin-blog/updateblog/${updatedBlog.blogId}`, updatedBlog);
      setBlogs(prevBlogs =>
        prevBlogs.map(blog =>
          blog._id === updatedBlog.blogId ? { ...blog, ...updatedBlog } : blog
        )
      );
      showSuccess('Blog updated successfully');
      closeEditModal();
    } catch (err) {
      console.error('Failed to update blog:', err.message);
      showError('Failed to update blog');
    }
  };

  const deleteBlog = async id => {
    try {
      await api.delete(`/admin-blog/${id}`);
      const updatedBlogs = blogs.filter(blog => blog._id !== id);
      setBlogs(updatedBlogs);
      showSuccess('Blog deleted successfully');
    } catch (err) {
      console.error('Failed to delete blog:', err.message);
      showError('Failed to delete blog');
    }
  };

  const openViewBlog = blog => {
    navigate(`/blog/view/${blog._id}`);
  };

  const createNewBlog = () => {
    navigate('/blog/');
  };

  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBlogs = filteredBlogs.slice(startIndex, endIndex);

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const Pagination = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 rounded hover:bg-gray-200 text-[10px] font-light">
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="ellipsis1" className="text-[10px] font-light">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded text-[10px] font-light ${
            currentPage === i ? 'bg-black text-white' : 'hover:bg-gray-200'
          }`}>
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis2" className="text-[10px] font-light">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 rounded hover:bg-gray-200 text-[10px] font-light">
          {totalPages}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center gap-2 my-4">
        <button
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded text-[10px] font-light ${
            currentPage === 1 ? 'text-gray-400' : 'hover:bg-gray-200'
          }`}>
          Prev
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded text-[10px] font-light ${
            currentPage === totalPages ? 'text-gray-400' : 'hover:bg-gray-200'
          }`}>
          Next
        </button>
      </div>
    );
  };

  const EmptyBlogsState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 text-gray-400">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </div>
      <h3 className="mb-2 text-sm font-light text-gray-600">No blogs found</h3>
      <p className="max-w-md mb-6 text-xs font-light text-gray-500">
        You haven't created any blogs yet. Start writing your first blog post to share your thoughts
        and ideas with your audience.
      </p>
      <button
        onClick={createNewBlog}
        className="px-6 py-2 text-xs font-light text-white transition-colors bg-black rounded hover:bg-gray-800">
        Create Your First Blog
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full p-4 pl-64 h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} data-testid="beat-loader" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-[calc(100vh-63px)] px-5 lg:p-5">
        <div className="flex items-center p-4 mb-4 text-red-800 border-t-4 border-red-300 bg-red-50">
          <div className="text-xs font-light ms-3">{error}</div>
          <button
            type="button"
            className="ms-auto -mx-1.5 -my-1.5 bg-red-50 text-red-500 rounded-lg focus:ring-2 focus:ring-red-400 p-1.5 hover:bg-red-200 inline-flex items-center justify-center h-8 w-8"
            onClick={() => setError(null)}>
            <span className="sr-only">Dismiss</span>
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6]">
      <div className="w-full px-5 bg-white lg:p-5">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-semibold">Manage My Blogs</h1>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={toggleFilterMenu}
                  className="bg-[#ECECEC] p-2 px-4 rounded text-sm font-light"
                  disabled={blogs.length < 1}>
                  Filter
                </button>
                {isFilterMenuOpen && (
                  <div className="absolute top-12 right-0 bg-white border rounded-lg shadow-lg p-2 w-[180px] z-10">
                    <p className="text-sm font-bold mb-2">Filter Blogs</p>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        className="w-4 h-4 accent-black"
                        checked={filter === 'featured'}
                        onChange={() => handleFilterChange('featured')}
                      />{' '}
                      Featured
                    </label>
                    <label className="flex items-center gap-2 mt-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        className="w-4 h-4 accent-black"
                        checked={filter === 'not-featured'}
                        onChange={() => handleFilterChange('not-featured')}
                      />{' '}
                      Not Featured
                    </label>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={toggleSortMenu}
                  className="bg-black text-white p-2 px-4 rounded text-sm"
                  disabled={blogs.length < 1}>
                  Sort
                </button>
                {isSortMenuOpen && (
                  <div className="absolute top-12 right-0 bg-white border rounded-lg shadow-lg p-2 w-[180px] z-10">
                    <p className="text-sm font-bold mb-2">Sort Blogs</p>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        className="w-4 h-4 accent-black"
                        checked={sort === 'newest'}
                        onChange={() => handleSortChange('newest')}
                      />{' '}
                      Newest
                    </label>
                    <label className="flex items-center gap-2 mt-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        className="w-4 h-4 accent-black"
                        checked={sort === 'oldest'}
                        onChange={() => handleSortChange('oldest')}
                      />{' '}
                      Oldest
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          {blogs.length === 0 ? (
            <EmptyBlogsState />
          ) : (
            <>
              <div className="w-full text-base font-medium flex items-center justify-between my-2 px-3 lg:pl-5 py-2 bg-[#ECECEC] text-[#979797] rounded">
                <p className="w-full lg:w-[50%]">Blogs</p>
                <p className="hidden lg:block w-full lg:w-[20%]">Status</p>
                <p className="hidden lg:block w-full lg:w-[20%]">Views</p>
                <p className="hidden lg:block w-full lg:w-[20%]">Action</p>
              </div>

              <div id="blogs-list" className="flex flex-col gap-2 whitespace-nowrap">
                {currentBlogs.map((blog, index) => (
                  <div
                    key={blog._id || index}
                    className="flex flex-col items-center w-full gap-2 p-3 duration-200 border-b border-gray-300 cursor-pointer lg:flex-row lg:justify-between lg:gap-0 lg:pl-5 hover:border-2 hover:rounded hover:border-black hover:shadow-xl delay-600">
                    <div className="w-full lg:w-[50%] flex items-start gap-8">
                      <div className="space-y-1 text-wrap">
                        <h3 className="text-sm font-medium" data-testid="blog-title">
                          {blog?.title?.length > 100
                            ? `${blog?.title?.substring(0, 50)}...`
                            : blog?.title}
                        </h3>
                        <div>
                          {blog.description ? (
                            <p className="text-sm font-normal text-gray-600">
                              {blog?.description?.length > 100
                                ? `${blog?.description?.substring(0, 50)}...`
                                : blog?.description}
                            </p>
                          ) : (
                            <div
                              className="text-sm font-light text-gray-800"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(
                                  blog.content?.length > 100
                                    ? `${blog.content.substring(0, 50)}...`
                                    : blog.content || ''
                                ),
                              }}
                            />
                          )}
                          <p className="text-sm font-light text-gray-500">
                            Date: {new Date(blog.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {blog.status === 'Active' ? (
                      <div className="w-full lg:w-[20%] flex items-center gap-1 text-green-400 whitespace-nowrap">
                        <AiOutlineCheckCircle className="w-5 h-5" />
                        <p className="text-sm font-normal">Featured</p>
                      </div>
                    ) : (
                      <div className="w-full lg:w-[20%] flex items-center gap-1 text-red-400 whitespace-nowrap">
                        <AiOutlineCloseCircle className="w-5 h-5" />
                        <p className="text-sm font-normal">Not Featured</p>
                      </div>
                    )}

                    <div className="w-full lg:w-[20%] flex items-center gap-2 text-[#979797] whitespace-nowrap">
                      <PiUsersThreeThin className="w-5 h-5" />
                      <p className="text-sm font-normal">{blog.views || 0} Views</p>
                    </div>

                    <div
                      id="actions"
                      className="w-full lg:w-[20%] flex items-center gap-2 whitespace-nowrap">
                      <button
                        className="w-[70%] h-10 flex items-center justify-center bg-[#ECECEC] text-sm rounded"
                        onClick={() => openViewBlog(blog)}>
                        View Blog
                      </button>

                      <div className="relative w-[30%]">
                        <button
                          onClick={() => toggleActionMenu(index)}
                          className="w-full h-10 flex items-center justify-center text-[#979797] rounded focus:outline-none">
                          <BsThreeDotsVertical className="w-5 h-5" />
                        </button>

                        {actionMenuOpen === index && (
                          <div className="absolute top-12 right-0 bg-white border rounded-lg shadow-lg w-[180px] p-2 z-10">
                            <p className="text-sm font-normal mb-2">Actions</p>
                            <button
                              className="w-full flex items-center text-left px-2 py-1 text-sm"
                              onClick={() => handleActionClick('edit', blog)}>
                              <IoIosAddCircleOutline className="w-8 h-8" />
                              Edit Post
                            </button>
                            <button
                              className="w-full flex items-center text-left px-2 py-1 text-sm text-red-500"
                              onClick={() => handleActionClick('delete', blog)}>
                              <RiDeleteBin6Line className="w-8 h-8" />
                              Delete Blog
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {blogs.length > 0 && <Pagination />}
            </>
          )}
        </div>

        <EditBlogModal
          blog={selectedBlog}
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          onSave={saveBlogChanges}
        />
      </div>
    </div>
  );
};

export default ManageBlogs;
